import {
  columnOptions,
  numericUnit,
  OUTFALL_TYPE_COLUMN_INDEX,
  outfallHasStageData,
  SECTION_SCHEMAS,
  sectionsForElement,
  type UnitKind,
} from "./sectionSchema.ts";
import { findOption } from "./options.ts";
import { readCurves } from "./curves.ts";
import { sameId } from "./idLookup.ts";
import { readSnowPacks } from "./snowpacks.ts";
import { unitSystemOf } from "../units.ts";
import { getElementCommentKey } from "./parseInp.ts";
import type { NodeKind, LinkKind, SwmmProject } from "./types.ts";

/**
 * Deliberately a separate table from `results/resultsVariables.ts`'s own
 * `unitLabelFor`, not a duplicate to merge — this one is keyed by `UnitKind`
 * (a `.inp` COLUMN's unit family, from `sectionSchema.ts`, for the property
 * panel's header row) while that one is keyed by `ResultVariableKey` (a
 * simulation RESULTS variable, for chart/report axis labels). The two
 * domains only partly overlap (both need length ft/m and rain-depth in/mm/
 * hr, for instance) but diverge in real, non-interchangeable ways: this one
 * has no volume/velocity/per-day entries results needs, that one has no
 * landarea/percent/hours entries a property column needs, and `flow` here
 * returns the RAW-case flow-units token (matching `.inp` column display)
 * where `unitLabelFor`'s own default lowercases it (reads better next to a
 * chart value) — a deliberate casing difference, not an oversight.
 */
export function formatUnit(kind: UnitKind, flowUnits: string): string {
  const sys = unitSystemOf(flowUnits);
  switch (kind) {
    case "length":
      return sys === "US" ? "ft" : "m";
    case "landarea":
      return sys === "US" ? "acres" : "ha";
    case "flow":
      return flowUnits;
    case "percent":
      return "%";
    case "depth":
      return sys === "US" ? "in" : "mm";
    case "rate":
      return sys === "US" ? "in/hr" : "mm/hr";
    case "perHour":
      return "1/hr";
    case "days":
      return "days";
    case "hours":
      return "hr";
    case "dimensionless":
      return "";
  }
}

/**
 * Real per-PROJECT reference lists, unlike `columnOptions`' fixed vocabulary
 * — a valid raingage/node/curve name is whatever THIS project defines.
 * Diverted Link is scoped to links whose FROM node is this divider itself
 * (a divider always has exactly two outflow links) — offering every link in
 * the project would mostly be noise. `undefined`, never an empty array,
 * whenever the project doesn't define any value of the right kind yet.
 */
/** `undefined`, never an empty array — matches `referenceOptions`' own doc comment above it on why an empty list means "not defined yet", not "defined as nothing". */
function namesOrUndefined(names: readonly string[]): readonly string[] | undefined {
  return names.length > 0 ? names : undefined;
}

function referenceOptions(
  project: SwmmProject,
  section: string,
  label: string,
  elementName: string
): readonly string[] | undefined {
  if (section === "SUBCATCHMENTS" && label === "Raingage") {
    return namesOrUndefined(project.raingages.map((r) => r.name));
  }
  if (section === "SUBCATCHMENTS" && label === "Outlet") {
    return namesOrUndefined([
      ...project.nodes.map((n) => n.name),
      ...project.subcatchments.map((s) => s.name),
    ]);
  }
  if (section === "SUBCATCHMENTS" && label === "Snow Pack") {
    return namesOrUndefined(readSnowPacks(project.rawSections).map((s) => s.name));
  }
  if (section === "DIVIDERS" && label === "Diverted Link") {
    return namesOrUndefined(
      project.links.filter((l) => l.fromNode === elementName).map((l) => l.name)
    );
  }
  if (section === "PUMPS" && label === "Pump Curve") {
    return namesOrUndefined(
      readCurves(project.rawSections)
        .filter((c) => c.type.toUpperCase().startsWith("PUMP"))
        .map((c) => c.name)
    );
  }
  return undefined;
}

export type ElementCategory = "node" | "link" | "subcatchment" | "raingage";

/** A discriminated union on `category` — `{ category: "node", kind: "pump" }` (a real LinkKind, not a NodeKind) is a compile error, not a silent mismatch. */
export type ElementRef =
  | { category: "node"; kind: NodeKind; name: string }
  | { category: "link"; kind: LinkKind; name: string }
  | { category: "subcatchment"; kind: "subcatchment"; name: string }
  | { category: "raingage"; kind: "raingage"; name: string };

interface PropertyGroup {
  section: string;
  label: string;
  properties: {
    label: string;
    value: string;
    columnIndex: number;
    options?: readonly string[];
    unitKind?: UnitKind;
  }[];
}

export function getPropertyGroups(project: SwmmProject, ref: ElementRef): PropertyGroup[] {
  const groups: PropertyGroup[] = [];
  const infiltrationMethod = findOption(project.rawSections, "INFILTRATION");
  for (const section of sectionsForElement(ref.kind, ref.category)) {
    const schema = SECTION_SCHEMAS[section];
    // sameId, not === : a companion section's row (XSECTIONS/SUBAREAS/
    // INFILTRATION/...) can legally spell this element's name with
    // different casing than its primary section row.
    const row = (project.rawSections.get(section) ?? []).find(
      (r) => r[0] && sameId(r[0], ref.name)
    );
    if (!schema || !row) continue;
    const columns =
      typeof schema.columns === "function"
        ? schema.columns(row, infiltrationMethod)
        : schema.columns;
    const sectionLabel =
      typeof schema.label === "function" ? schema.label(infiltrationMethod) : schema.label;
    groups.push({
      section,
      label: sectionLabel,
      properties: columns.map((label, columnIndex) => ({
        label,
        value: row[columnIndex + 1] ?? "",
        columnIndex,
        options:
          columnOptions(section, label) ?? referenceOptions(project, section, label, ref.name),
        unitKind: numericUnit(section, label, infiltrationMethod),
      })),
    });
  }
  return groups;
}

/**
 * The selected element's own trailing `;comment`, if the `.inp` it came
 * from had one — looked up against `sectionsForElement`'s own FIRST
 * (primary/defining) section, never a companion one (XSECTIONS/SUBAREAS/
 * INFILTRATION), since that's the row a real author would actually put a
 * per-element description on. `comments` is `undefined` for a project
 * created fresh (nothing to look up) or opened before this feature
 * existed.
 */
export function getElementComment(
  comments: ReadonlyMap<string, string> | undefined,
  ref: ElementRef
): string | undefined {
  if (!comments || comments.size === 0) return undefined;
  const [primarySection] = sectionsForElement(ref.kind, ref.category);
  return comments.get(getElementCommentKey(primarySection, ref.name));
}

/** An OUTFALLS row's column COUNT depends on its own Outfall Type — a FREE/NORMAL row has no Stage/Table/Timeseries column at all, so editing Type into or out of {FIXED,TIDAL,TIMESERIES} must insert/remove that slot, not just overwrite row[2] in place (which would leave Tide Gate's value at the wrong index). */
function restructureOutfallRow(row: string[], newType: string): string[] {
  const hadStageData = outfallHasStageData(row[2] ?? "");
  const hasStageData = outfallHasStageData(newType);
  const next = [...row];
  next[2] = newType;
  if (!hadStageData && hasStageData) next.splice(3, 0, "0");
  else if (hadStageData && !hasStageData) next.splice(3, 1);
  return next;
}

/** `columnIndex` is 0-based over the schema's own `columns` — one less than the row's real index, since row[0] is always the element's name. A row shorter than the edited column is padded with "0" first, matching SWMM's own missing-trailing-field default. A no-op if the element has no such row. */
export function setPropertyValue(
  raw: ReadonlyMap<string, string[][]>,
  section: string,
  elementName: string,
  columnIndex: number,
  value: string
): ReadonlyMap<string, string[][]> {
  const rows = raw.get(section);
  // Same case-insensitive match getPropertyGroups uses to FIND this row —
  // without it, an edit to a case-mismatched row would silently no-op.
  const rowIndex = rows?.findIndex((r) => r[0] && sameId(r[0], elementName)) ?? -1;
  if (!rows || rowIndex === -1) return raw;

  const newRows = rows.map((row, i) => (i === rowIndex ? [...row] : row));
  if (section === "OUTFALLS" && columnIndex === OUTFALL_TYPE_COLUMN_INDEX) {
    newRows[rowIndex] = restructureOutfallRow(newRows[rowIndex], value);
  } else {
    const row = newRows[rowIndex];
    while (row.length <= columnIndex + 1) row.push("0");
    row[columnIndex + 1] = value;
  }

  const newRaw = new Map(raw);
  newRaw.set(section, newRows);
  return newRaw;
}

/**
 * Same effect as calling `setPropertyValue` once per name in `elementNames`,
 * but in a single pass over `rows` — the naive loop re-copied the WHOLE
 * section array (`rows.map(...)` inside every `setPropertyValue` call) once
 * per selected element, O(section size) work times O(selection size); at
 * selection ~= section size (e.g. select-all-junctions, then bulk-edit) that
 * was O(n²) — a real multi-second freeze on a 40k-element network (P1 deep-
 * review). `sameId`'s own case-insensitive match is replicated via a
 * pre-uppercased `Set`, same semantics as the per-call version.
 */
export function setPropertyValueForMany(
  raw: ReadonlyMap<string, string[][]>,
  section: string,
  elementNames: string[],
  columnIndex: number,
  value: string
): ReadonlyMap<string, string[][]> {
  const rows = raw.get(section);
  if (!rows || elementNames.length === 0) return raw;
  const targetNames = new Set(elementNames.map((n) => n.toUpperCase()));
  let changed = false;
  const newRows = rows.map((row) => {
    if (!row[0] || !targetNames.has(row[0].toUpperCase())) return row;
    changed = true;
    if (section === "OUTFALLS" && columnIndex === OUTFALL_TYPE_COLUMN_INDEX) {
      return restructureOutfallRow(row, value);
    }
    const next = [...row];
    while (next.length <= columnIndex + 1) next.push("0");
    next[columnIndex + 1] = value;
    return next;
  });
  if (!changed) return raw;
  const newRaw = new Map(raw);
  newRaw.set(section, newRows);
  return newRaw;
}
